// GROUND GEN CPU PREP (groundgen.js, gs-gpupainter): the per-world / per-slot work of the device
// ground painter that stays on the CPU because it is no loop over texels the GPU could run — the
// genome records' texture params (an iterative solve a record) and their per-texel constants, a
// slot's ways params (groundways waysTile: its stair treads' median pitch; markCells: the coarse
// marks), the ground-tree nodes' decode (codec + lookup index + the TIN's triangles + its vertex
// array) and a slot's crown discs (groundref crownDiscsOf on the level-j drawn ground -> the disc
// instance array). PURE (no DOM, no GL): run in the painter's worker (groundwork.js, inlined by
// scripts/build-client.js as __GG_WORKER_SRC__) or, without one, on the main thread — the same
// code, the same numbers. The results are plain objects + typed arrays (structured-clone safe).
import { prepWays, waysTile, markCells, WAYS_K } from './groundways.js';
import { genomeTexParams, genomeTexelConsts } from './genometex.js';
import { decodeNode, meshOf } from '../../groundm/codec.js';
import { indexNode, groundAt } from '../../groundm/lookup.js';
import { crownDiscsOf } from './groundref.js';

// prepWays' static look (its constants) on an empty 1 x 1 field: a slot's field window replaces it
export function staticWays(ids, noneId, treelineM, empty, look, marks, pal) {
  return prepWays(Uint8Array.from(empty), 1, 1, look, marks, { pal, ids, noneId, treelineM });
}

// the genome records' texture params + their constants at each texel (genomeTexParams,
// genomeTexelConsts) -> { [key]: { P, C: [[texel, consts], ...] } }
export function genomeCpu(recs, texels) {
  const out = {};
  for (const k of Object.keys(recs)) {
    const P = genomeTexParams(recs[k], k);
    out[k] = { P, C: texels.map((t) => [t, genomeTexelConsts(P, t)]) };
  }
  return out;
}

// a slot's ways params from its field window (8 bytes a cell, W1 x H1; the slot at window-local
// (x0, z0), S m, px texels): null when no way lies within half a cell of the span (waysTile's
// null, read off the bytes first), else waysTile's scalars, its tread ka ([byte, ka] pairs) and
// mark: whether a cell carries a mark code, + the coarse regime's mark cells (mq: window cell
// index, mv: 4 floats each; markCells)
export function waysCpu(ST0, field, W1, H1, x0, z0, S, px) {
  let rmin = 255, marks = false;
  const R0 = WAYS_K.R_OFF + WAYS_K.R_SCALE / 2;
  for (let o = 0; o < field.length; o += 8) {
    if (field[o] < rmin) rmin = field[o];
    if ((field[o + 6] & WAYS_K.CMASK) >= WAYS_K.MARK0) marks = true;
  }
  if (rmin >= R0) return null;
  const st = { ...ST0, field, W1, H1, ox: 0, oz: 0, ow: W1, oh: H1 };   // the window is the whole field (ST0's is 1 x 1)
  const wt = waysTile(st, x0, z0, S, S, px, px);
  if (!wt) return null;
  if (marks && !st.ground) throw new Error('ways field holds ground marks (codes 5-7) but world.json ways_field.marks has no ground — rerun the WORLD stage');   // markCells' own throw
  let mq = null, mv = null;
  if (marks && !wt.fine) {
    const mk = markCells(st);
    mq = new Int32Array(mk.size);
    mv = new Float32Array(4 * mk.size);
    let i = 0;
    for (const [q, v] of mk) { mq[i] = q; mv.set(v, 4 * i); i++; }
  }
  return {
    fine: wt.fine, kc: wt.kc, pk: wt.pk, amp: wt.amp, ea: wt.ea, eb: wt.eb, wa: wt.wa, wb: wt.wb, hh: wt.hh, wn: wt.wn,
    treadKa: [...wt.treadKa], mark: marks, mq, mv,
  };
}

// WAYS-Q4: a slot past the 1 m field from its 4 m mass window (cells: w x h x Q4.B, the slot at window-local (x0, z0) m):
// null when no strip comes within half a cell (waysTile's), else waysTile's scalars (no marks, no tread ka at a 4 m mass)
export function q4Cpu(ST0, cells, w, h, x0, z0, S, px) {
  const wt = waysTile({ ...ST0, q4: true, cells, QW: w, QH: h, ox: 0, oz: 0, ow: w, oh: h, markCells: new Map() }, x0, z0, S, S, px, px);
  if (!wt) return null;
  return { fine: false, kc: wt.kc, pk: wt.pk, amp: wt.amp, ea: wt.ea, eb: wt.eb, wa: wt.wa, wb: wt.wb, hh: wt.hh, wn: wt.wn,
    treadKa: [], mark: false, mq: null, mv: null };
}

// a ground-tree node (its .bin body) at level j, tile (tx, ty): the node-local mesh (codec meshOf,
// indexed for groundAt), the TIN's triangles (NOSTAND / degenerate dropped), ymin, yn = floor(ymin)
// and the vertex array the GPU takes (x, z, y - yn: float32, node-local)
export function nodeCpu(body, j, tx, ty) {
  const mesh = indexNode({ ...meshOf(decodeNode(new Uint8Array(body))), level: j });
  mesh.tx = tx; mesh.ty = ty;
  const { x, z, y, idx, lab } = mesh, keep = [];
  for (let t = 0; t < idx.length / 3; t++) {
    if (lab && lab[t] & 16) continue;                                  // NOSTAND
    const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
    const den = (x[b] - x[a]) * (z[c] - z[a]) - (z[b] - z[a]) * (x[c] - x[a]);
    if (Math.abs(den) > 1e-12) keep.push(a, b, c);
  }
  let ymin = Infinity;
  for (let v = 0; v < y.length; v++) if (y[v] < ymin) ymin = y[v];
  const yn = Math.floor(ymin), nv = y.length, va = new Float32Array(3 * nv);
  for (let v = 0; v < nv; v++) { va[3 * v] = x[v]; va[3 * v + 1] = z[v]; va[3 * v + 2] = y[v] - yn; }
  return { mesh, tri: Uint32Array.from(keep), ymin, yn, va };
}

// the bytes a decoded object holds (typed arrays exact, arrays 8 a slot; the node index's buckets)
export function bytesOf(d, depth = 0) {
  if (!d) return 64;
  if (ArrayBuffer.isView(d)) return d.byteLength;
  let n = 64;
  if (depth > 3) return n;
  for (const v of Object.values(d)) {
    if (ArrayBuffer.isView(v)) n += v.byteLength;
    else if (Array.isArray(v)) { n += 8 * v.length; for (const e of v) if (Array.isArray(e)) n += 32 + 8 * e.length; }
    else if (v && typeof v === 'object') n += bytesOf(v, depth + 1);
    else n += 8;
  }
  return n;
}

// a slot's crown discs: the crown / shrub packs ([pack, shrub] in groundref crownDiscsOf's order:
// the tree packs, then the shrub packs; each pack's crowns inside span [xa, xb, za, zb]), y = the
// level-j drawn ground (nodes: the level-j meshes by (tx, ty), groundref crownDiscs' 4-node rule)
// -> the disc instance array (7 floats a disc: x - X0, z - Z0, r, cls, y + top - dyref,
// y + base - dyref, dome), nd, dyref = floor(min y)
export function discsCpu(C, nodes, packs, span, j, X0, Z0) {
  const S = 128 * 2 ** j, off = 0.25 * (1 << j), byKey = new Map(), nk = (a, b) => (a + 32768) * 65536 + (b + 32768);
  for (const m of nodes) byKey.set(nk(m.tx, m.ty), m);
  const yAt = (x, z) => {
    const tx = Math.floor((x - off) / S), ty = Math.floor((z - off) / S);
    let n = byKey.get(nk(tx, ty)), v = n ? groundAt(n, x - tx * S, z - ty * S) : NaN;
    if (v === v) return v;
    n = byKey.get(nk(tx - 1, ty)); v = n ? groundAt(n, x - (tx - 1) * S, z - ty * S) : NaN;
    if (v === v) return v;
    n = byKey.get(nk(tx, ty - 1)); v = n ? groundAt(n, x - tx * S, z - (ty - 1) * S) : NaN;
    if (v === v) return v;
    n = byKey.get(nk(tx - 1, ty - 1)); v = n ? groundAt(n, x - (tx - 1) * S, z - (ty - 1) * S) : NaN;
    return v;
  };
  const [xa, xb, za, zb] = span, trees = [], shrubs = [];
  for (const [p, shrub] of packs) {
    const keep = [];
    for (let k = 0; k < p.n; k++) if (p.x[k] >= xa && p.x[k] <= xb && p.z[k] >= za && p.z[k] <= zb) keep.push(k);
    if (!keep.length) continue;
    const pick = (a) => (a ? Array.from(keep, (k) => a[k]) : null);
    (shrub ? shrubs : trees).push({ n: keep.length, x: pick(p.x), z: pick(p.z), h: pick(p.h), r: shrub ? null : pick(p.r), appear: shrub ? null : pick(p.appear) });
  }
  // crownDiscsOf takes every tree pack before every shrub pack: the order the packs come in
  // (packs lists the trees first), one call
  const D = crownDiscsOf(C, trees, shrubs, yAt), nd = D.x.length;
  let ymin = Infinity;
  for (let d = 0; d < nd; d++) if (D.y[d] < ymin) ymin = D.y[d];
  const dyref = nd ? Math.floor(ymin) : 0, a = new Float32Array(7 * nd);
  for (let d = 0, o = 0; d < nd; d++, o += 7) {
    a[o] = D.x[d] - X0; a[o + 1] = D.z[d] - Z0; a[o + 2] = D.r[d]; a[o + 3] = D.cls[d];
    a[o + 4] = D.y[d] + D.top[d] - dyref; a[o + 5] = D.y[d] + D.base[d] - dyref; a[o + 6] = D.dome[d] ? 1 : 0;
  }
  return { a, nd, dyref };
}
